/**
 * BM-H3468 — Remove (hide) a matter from Open Matters, or restore it.
 *
 * Vanessa, 2 Oct 2026, chose "Remove (hide)" over permanent erasure: the
 * matter leaves the list, every record is kept, the server writes an audit
 * row with the reason, and a director or client care can restore it.
 * The server enforces who may do this; the page only hides the button
 * from people who would be refused.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { Button } from "./ui/button";

function errorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  return err?.message || fallback;
}

export default function OpenMatterRemoveButton({ matterRef, mode = "remove", onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const enc = encodeURIComponent(matterRef);

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      if (mode === "restore") {
        await axios.post(`${API}/matters/${enc}/restore`);
      } else {
        await axios.post(`${API}/matters/${enc}/remove`, { reason: reason.trim() });
      }
      setOpen(false);
      setReason("");
      if (onDone) onDone();
    } catch (e) {
      setErr(errorText(e, mode === "restore" ? "Restore failed" : "Remove failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs px-2 py-1 rounded border border-navy-border text-txt-muted hover:text-white hover:border-slate-400"
        data-testid={`${mode}-matter-${matterRef}`}
      >
        {mode === "restore" ? "Restore" : "Remove"}
      </button>
    );
  }

  const tooShort = mode === "remove" && reason.trim().length < 3;
  return (
    <div className="flex flex-col gap-2 min-w-[220px] text-left" data-testid={`${mode}-matter-confirm-${matterRef}`}>
      <p className="text-xs text-txt">
        {mode === "restore"
          ? `Put ${matterRef} back on Open Matters?`
          : `Remove ${matterRef} from Open Matters? Nothing is deleted and it can be restored.`}
      </p>
      {mode === "remove" && (
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (required)"
          maxLength={500}
          className="h-8 rounded-md border border-navy-border text-xs px-2 bg-navy-hover text-white"
          data-testid={`remove-matter-reason-${matterRef}`}
        />
      )}
      {err && <p className="text-xs text-status-red">{err}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={busy || tooShort}>
          {busy ? "Working…" : mode === "restore" ? "Restore" : "Remove"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setErr(""); }} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
